"use client";

import {
	AlertCircle,
	Bell,
	BellOff,
	Calendar,
	CheckCircle,
	Clock,
	Crown,
	LogOut,
	QrCode,
	Settings,
	Star,
	User,
	XCircle,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import type { NotificationMessage } from "@/hooks/useNotifications";
import { useUserInfo } from "@/hooks/useUserInfo";
import { cn } from "@/lib/utils";
import type { VipInfo } from "@/types/vip";
import { VipLevel } from "@/types/vip";
import EmailManagement from "@/components/notifications/EmailManagement";

interface ExpirationInfo {
	expired: boolean;
	text: string;
	dateText?: string;
	isLowTime?: boolean;
}

interface UserAccountPanelProps {
	language: string;
	principal: string;
	btcAddress?: string;
	isVip: boolean;
	vipInfo: VipInfo;
	vipColors: {
		textColor?: string;
		badgeColor?: string;
	};
	notifications: NotificationMessage[];
	unreadCount: number;
	isNotificationsLoading: boolean;
	onMarkAllAsRead: () => Promise<void>;
	onMarkAsRead: (notificationId: string) => Promise<void>;
	onLogout: () => void;
	onClose: () => void;
	onOpenAssets: () => void;
	expirationInfo: ExpirationInfo | null;
	className?: string;
	notificationLimit?: number;
	notificationViewportClassName?: string;
}

const getNotificationIcon = (type: NotificationMessage["type"]) => {
	switch (type) {
		case "ORDER_COMPLETED":
			return <CheckCircle className="h-4 w-4 text-green-500" />;
		case "ORDER_FAILED":
			return <XCircle className="h-4 w-4 text-red-500" />;
		case "ORDER_CANCELLED":
			return <Clock className="h-4 w-4 text-gray-500" />;
		case "PRICE_ALERT":
			return <AlertCircle className="h-4 w-4 text-blue-500" />;
		default:
			return <Bell className="h-4 w-4 text-gray-500" />;
	}
};

export function UserAccountPanel({
	language,
	principal,
	isVip,
	vipInfo,
	vipColors,
	notifications,
	unreadCount,
	isNotificationsLoading,
	onMarkAllAsRead,
	onMarkAsRead,
	onLogout,
	onClose,
	onOpenAssets,
	expirationInfo,
	className,
	notificationLimit = 4,
	notificationViewportClassName = "h-[260px]",
}: UserAccountPanelProps) {
	const router = useRouter();
	const { toast } = useToast();
	const visibleNotifications = notifications.slice(0, notificationLimit);
	const { userInfo } = useUserInfo({
		principal,
		enabled: Boolean(principal),
	});

	const formatPrincipal = (value: string) => {
		if (value.length <= 8) return value;
		return `${value.slice(0, 5)}...${value.slice(-3)}`;
	};
	const displayName =
		userInfo?.username?.trim() ||
		(language === "zh" ? "我的账户" : "My Account");

	const formatTime = (dateString: string) => {
		const date = new Date(dateString);
		const now = new Date();
		const diffInHours = Math.floor(
			(now.getTime() - date.getTime()) / (1000 * 60 * 60),
		);

		if (diffInHours < 1) {
			return language === "zh" ? "刚刚" : "Just now";
		}
		if (diffInHours < 24) {
			return language === "zh" ? `${diffInHours}小时前` : `${diffInHours}h ago`;
		}

		return date.toLocaleDateString(language === "zh" ? "zh-CN" : "en-US");
	};

	const handleNotificationClick = async (notification: NotificationMessage) => {
		if (!notification.read) {
			await onMarkAsRead(notification.id);
		}

		onClose();

		if (notification.actionUrl) {
			router.push(notification.actionUrl);
			return;
		}

		router.push("/profile?tab=notifications");
	};

	const handleMarkAllAsRead = async () => {
		await onMarkAllAsRead();
		toast({
			title:
				language === "zh"
					? "已标记所有通知为已读"
					: "All notifications marked as read",
			duration: 2000,
		});
	};

	const handleOpenAssets = () => {
		onClose();
		onOpenAssets();
	};

	return (
		<div className={cn("flex flex-col overflow-hidden", className)}>
			<div className="bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900">
				<div className="flex items-start gap-3">
					<div className="min-w-0">
						<div className="flex items-center gap-2">
							<div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm dark:bg-slate-100 dark:text-slate-900">
								<User className={cn("h-5 w-5", vipColors.textColor)} />
							</div>
							<div className="min-w-0">
								<div className="flex items-center gap-2">
									<h3 className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
										{displayName}
									</h3>
									<Link
										href="/profile"
										onClick={onClose}
										className="inline-flex h-7 shrink-0 items-center rounded-lg bg-light-primary px-2.5 text-[11px] font-medium text-white shadow-sm transition-colors hover:bg-light-primary/90 dark:bg-dark-primary dark:hover:bg-dark-primary/90"
									>
										{language === "zh" ? "前往个人中心" : "Go to profile"}
									</Link>
									{unreadCount > 0 && (
										<Badge
											variant="destructive"
											className="h-5 min-w-5 rounded-full px-1.5 text-[10px]"
										>
											{unreadCount > 99 ? "99+" : unreadCount}
										</Badge>
									)}
								</div>
								<button
									type="button"
									onClick={() => void handleOpenAssets()}
									className="mt-1 inline-flex max-w-full items-center gap-1 rounded-lg border border-transparent bg-white/50 px-2 py-1 text-left font-mono text-xs text-slate-500 transition-colors hover:border-light-primary/20 hover:bg-white hover:text-slate-700 dark:bg-slate-900/50 dark:hover:bg-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
								>
									<span className="truncate">
										{language === "zh" ? "身份ID" : "Identity ID"}: {formatPrincipal(principal)}
									</span>
									<QrCode className="h-3.5 w-3.5 shrink-0 text-slate-400" />
								</button>
							</div>
						</div>
					</div>
				</div>

				<div className="mt-3 flex flex-wrap items-center gap-2">
					{isVip ? (
						<span
							className={cn(
								"inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
								vipColors.badgeColor,
							)}
						>
							{vipInfo.level === VipLevel.LEVEL_1 && (
								<Crown className="h-3.5 w-3.5 text-amber-500" />
							)}
							{vipInfo.level === VipLevel.LEVEL_2 && (
								<Crown className="h-3.5 w-3.5 text-yellow-500" />
							)}
							{vipInfo.name}
						</span>
					) : (
						<span className="inline-flex items-center rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
							{language === "zh" ? "探索者" : "Explorer"}
						</span>
					)}
					{expirationInfo?.text && (
						<span
							className={cn(
								"inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs",
								expirationInfo.expired || expirationInfo.isLowTime
									? "bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-300"
									: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300",
							)}
						>
							<Calendar className="h-3.5 w-3.5" />
							{expirationInfo.text}
						</span>
					)}
				</div>
				{isVip && vipInfo.description && (
					<p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
						{vipInfo.description}
					</p>
				)}
				{expirationInfo?.dateText && (
					<p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
						{expirationInfo.dateText}
					</p>
				)}
			</div>

			<Separator />

			<div className="flex items-center justify-between px-4 py-3">
				<div className="flex items-center gap-2">
					<Bell className="h-4 w-4 text-slate-500" />
					<div>
						<p className="text-sm font-medium text-slate-900 dark:text-slate-100">
							{language === "zh" ? "通知" : "Notifications"}
						</p>
						<p className="text-xs text-slate-500 dark:text-slate-400">
							{language === "zh"
								? `${unreadCount} 条未读`
								: `${unreadCount} unread`}
						</p>
					</div>
				</div>
				{unreadCount > 0 && (
					<Button
						variant="ghost"
						size="sm"
						onClick={handleMarkAllAsRead}
						className="h-8 rounded-xl px-2 text-xs"
					>
						{language === "zh" ? "全部已读" : "Mark all read"}
					</Button>
				)}
			</div>

			<ScrollArea className={cn(notificationViewportClassName, "px-4 pb-2")}>
				<div className="space-y-2 pb-2">
					{isNotificationsLoading ? (
						<div className="flex items-center justify-center py-8">
							<div className="h-7 w-7 animate-spin rounded-full border-2 border-slate-300 border-b-transparent dark:border-slate-600 dark:border-b-transparent" />
						</div>
					) : visibleNotifications.length > 0 ? (
						visibleNotifications.map((notification) => (
							<button
								key={notification.id}
								type="button"
								onClick={() => void handleNotificationClick(notification)}
								className={cn(
									"w-full rounded-2xl border px-3 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-900",
									notification.read
										? "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950"
										: "border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/20",
								)}
							>
								<div className="flex items-start gap-3">
									<div className="mt-0.5 shrink-0">
										{getNotificationIcon(notification.type)}
									</div>
									<div className="min-w-0 flex-1">
										<div className="flex items-start justify-between gap-2">
											<p className="line-clamp-1 text-sm font-medium text-slate-900 dark:text-slate-100">
												{notification.title}
											</p>
											{!notification.read && (
												<span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
											)}
										</div>
										<p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
											{notification.content}
										</p>
										<p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500">
											{formatTime(notification.createdAt)}
										</p>
									</div>
								</div>
							</button>
						))
					) : (
						<div className="flex flex-col items-center justify-center py-10 text-center">
							<BellOff className="h-10 w-10 text-slate-300 dark:text-slate-700" />
							<p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">
								{language === "zh" ? "暂无通知" : "No notifications"}
							</p>
							<p className="mt-1 max-w-[18rem] text-xs leading-5 text-slate-400 dark:text-slate-500">
								{language === "zh"
									? "挂单执行、价格预警和系统消息会显示在这里。"
									: "Order updates, price alerts, and system messages will appear here."}
							</p>
						</div>
					)}
				</div>
			</ScrollArea>

			<Separator />

			<div className="grid grid-cols-2 gap-2 p-4">
				<EmailManagement userPrincipal={principal} className="w-full justify-start" />
				<Button
					variant="ghost"
					size="sm"
					onClick={() => {
						onClose();
						router.push("/profile?tab=notifications");
					}}
					className="h-9 justify-start rounded-xl text-xs"
				>
					<Settings className="mr-2 h-4 w-4" />
					{language === "zh" ? "通知管理" : "Notification settings"}
				</Button>
				<Link
					href="/profile"
					onClick={onClose}
					className="inline-flex h-9 items-center justify-start rounded-xl px-3 text-xs transition-colors hover:bg-slate-100 dark:hover:bg-slate-900"
				>
					<User className="mr-2 h-4 w-4" />
					{language === "zh" ? "账户详情" : "Account details"}
				</Link>
				<button
					type="button"
					onClick={() => {
						onClose();
						onLogout();
					}}
					className="inline-flex h-9 items-center justify-start rounded-xl px-3 text-xs text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-950/30"
				>
					<LogOut className="mr-2 h-4 w-4" />
					{language === "zh" ? "退出登录" : "Sign out"}
				</button>
			</div>
		</div>
	);
}
